import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Toggle from '@cloudscape-design/components/toggle';
import {
  ALL_COUNTRIES,
  ALL_CURRENCIES,
  ALL_TENANT_REGIONS,
  AdminRole,
  TaxIdValidator,
  defaultRegionForCountry,
  TenantLocaleSchema,
  resolveMarketConfig,
  type Country,
  type Currency,
  type MarketConfig,
  type TenantLocale,
  type TenantRegion,
} from '@etiquetachef/types';
import { setMarketConfigCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { AdminConfirm } from '@/components/ui/AdminConfirm';
import { useAdminRole } from '@/auth/useAdminRole';
import { countryDisplayName, flagEmoji } from './worldCountries';
import { regionLabelKey } from './regionLabels';
import { MarketPspFields } from '@/features/billing/MarketPspFields';
import { marketPspInitial, marketPspPayload, type MarketPspValue } from '@/features/billing/marketPspValue';

type Props = {
  open: boolean;
  country: string | null;
  current: MarketConfig | null;
  /** Empresas ATIVAS neste país. > 0 bloqueia desativar (espelha o guard
   *  server `country_has_tenants`). */
  activeTenantCount: number;
  onClose: () => void;
};

const CURRENCY_OPTIONS = ALL_CURRENCIES.map((c) => ({ value: c, label: c }));
const LANGUAGE_OPTIONS = TenantLocaleSchema.options.map((l) => ({ value: l, label: l }));

/**
 * Sprint 40 — editor de catálogo de um país (aba Países). Edita
 * `/config/markets/items/{country}` via `setMarketConfig`: catálogo
 * (nome/telefone/moeda/idioma/tax-id), toggles (active/otp/payments) e, quando
 * vende, o roteamento PSP/fiscal.
 *
 * Origem: sem issue — commit a0529e97c, 2026-06-11 14:58 +0200 (Sprint 36 Fase 2c).
 *
 * Contrato: pré-preenche com o doc ou, para país conhecido (BR/MX/US/CA/IT),
 * com os defaults compilados (`resolveMarketConfig`/`TaxIdValidator`).
 * `validate` bloqueia o envio sem nome, com DDI fora de `+N…`, sem moeda, com
 * regex de tax-id inválido, vendável sem PSP ou sem entidade vendedora, e sem
 * motivo. Depois do `AdminConfirm`, uma chamada `setMarketConfig`; campo vazio
 * não é enviado (o servidor mantém o resolvido). Desativar fica travado na UI
 * quando `activeTenantCount > 0`; o servidor (`country_has_tenants`) recusa com
 * QUALQUER tenant do país, inclusive suspenso ou excluído — a trava da UI é
 * mais frouxa que a do servidor (achado da #926).
 *
 * Usado por: `features/countries/CountriesTab.tsx` (Editar, e Configurar na
 * linha sem doc — `current` nulo, o primeiro save cria o doc).
 *
 * Arquitetura: escrita só pela callable (`apps/functions/src/admin/setMarketConfig.ts`);
 * `match /config/markets/items/{country}` nega escrita do cliente. Os campos
 * de PSP são os mesmos de `/billing/markets` (`features/billing/MarketPspFields`).
 * Spec: `docs/screens/countries-admin.md`.
 *
 * Dados: entrada e resposta do `setMarketConfig`; resposta sem `payment_mode`
 * indica servidor anterior ao #756 (o modo pode ter sido gravado como live) e
 * vira um toast de alerta.
 *
 * Histórico:
 *  - 2026-10-01 11:33 +0200 — (#756, A7) os campos de PSP passam a ser o
 *    `MarketPspFields` compartilhado com `/billing/markets`: PSP ativo × modo
 *    (STRIPE / STRIPE SANDBOX / ASAAS / ASAAS SANDBOX — Asaas só no BR),
 *    permitidos por venda, fiscal derivado e entidade em lista. Saíram os
 *    guards locais "UE→Paddle"/"IT→Paddle MoR", que o owner revogou (Stripe é
 *    universal) — quem valida é o `setMarketConfig`.
 *  - 2026-10-13 11:23 +0200 — (#958) sem entidade vendedora no país: a PJ sai
 *    do PSP (`MarketPspFields` só a mostra).
 *  - 2026-10-13 11:30 +0200 — (#957) país sem doc ("Não configurado" na aba
 *    Países) abre com a moeda resolvida; o idioma ganha `de` (#86).
 *  - 2026-10-29 10:20 +0100 — (#1078, F2 da #960) Select de região ("Padrão do grupo" / São
 *    Paulo / Bélgica), desabilitado fora de ROOT; `region` só vai no payload quando mudou
 *    (`null` apaga o override).
 */
export function CountryEditDialog({
  open,
  country,
  current,
  activeTenantCount,
  onClose,
}: Props): React.JSX.Element | null {
  const { t, i18n } = useTranslation();

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [currency, setCurrency] = useState<Currency | ''>('');
  const [language, setLanguage] = useState<TenantLocale | ''>('');
  // Fusos IANA, um por linha, em ordem de uso (o primeiro é o default). String
  // de edição livre; parseada no submit. O servidor revalida cada zona (IANA).
  const [timezones, setTimezones] = useState('');
  const [taxName, setTaxName] = useState('');
  const [taxRegex, setTaxRegex] = useState('');
  const [taxPlaceholder, setTaxPlaceholder] = useState('');
  const [taxRequired, setTaxRequired] = useState(true);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [active, setActive] = useState(true);
  const [saleable, setSaleable] = useState(false);
  const [psp, setPsp] = useState<MarketPspValue>({ choice: '', extraAllowed: [], initial: null });
  // #1078 (F2 da #960): '' = padrão do grupo (sem override); só ROOT muda.
  const [region, setRegion] = useState<TenantRegion | ''>('');
  const role = useAdminRole();
  const isRoot = role === AdminRole.ROOT;
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasActiveTenants = activeTenantCount > 0;

  useEffect(() => {
    if (!open || !country) return;
    // Países conhecidos (BR/MX/US/CA/IT) raramente têm o catálogo preenchido —
    // os defaults vivem COMPILADOS em packages/types. Sem isto, editar o Brasil
    // mostra campos vazios (DDI/moeda/idioma/CNPJ/placeholder) mesmo o sistema
    // usando os defaults. Pré-preenche com o resolvido quando o doc não traz.
    const known = (ALL_COUNTRIES as readonly string[]).includes(country);
    const resolved = resolveMarketConfig(country, current ?? undefined);
    const taxSpec = known ? TaxIdValidator.specFor(country as Country) : null;

    setName(current?.name ?? countryDisplayName(country, i18n.language));
    setPhone(current?.phone_country_code ?? (known ? resolved.dialCode ?? '' : ''));
    // #957: país "Não configurado" (sem doc) abre com a moeda que o
    // "Configurar selecionados" gravaria (`currencyForCountry`) — sem isto o
    // editor exigia escolher a moeda de cada um dos 225 à mão.
    setCurrency(current?.currency ?? (known || current === null ? resolved.currency : ''));
    setLanguage(current?.language ?? (known ? resolved.language : ''));
    // `resolved.timeZones` já funde catálogo > compilado (país conhecido) > []
    // (país novo). Um fuso por linha preserva a ordem (primeiro = default).
    setTimezones(resolved.timeZones.join('\n'));
    setTaxName(current?.tax_id_name ?? taxSpec?.label ?? '');
    setTaxRegex(current?.tax_id_regex ?? '');
    setTaxPlaceholder(current?.tax_id_placeholder ?? taxSpec?.placeholder ?? '');
    setTaxRequired(current?.tax_id_required ?? true);
    setOtpEnabled(current?.otp_enabled ?? false);
    setActive(current?.active ?? true);
    setSaleable(current?.saleable ?? false);
    setPsp(marketPspInitial(country, current));
    setRegion(current?.region ?? '');
    setReason('');
    setError(null);
    setConfirmOpen(false);
  }, [open, current, country, i18n.language]);

  if (!open || !country) return null;
  const countryCode: string = country;
  const regionOptions = [
    {
      value: '',
      label: t('admin.countries.catalog.region.defaultOption', {
        region: t(regionLabelKey(defaultRegionForCountry(countryCode))),
      }),
    },
    ...ALL_TENANT_REGIONS.map((r) => ({ value: r, label: t(regionLabelKey(r)) })),
  ];

  /** Primeira mensagem de erro do formulário, ou `null` se pode salvar. */
  function validate(): string | null {
    if (name.trim().length === 0) return t('admin.countries.catalog.errors.nameRequired');
    if (phone.trim().length > 0 && !/^\+\d{1,4}$/.test(phone.trim()))
      return t('admin.countries.catalog.errors.phoneInvalid');
    if (currency === '') return t('admin.countries.catalog.errors.currencyRequired');
    if (taxRegex.trim().length > 0) {
      try {
        new RegExp(taxRegex.trim());
      } catch {
        return t('admin.countries.catalog.errors.regexInvalid');
      }
    }
    // #958 (2026-10-13 11:23 +0200): o país não escolhe entidade — a PJ sai do
    // PSP; o servidor recusa PSP sem entidade ativa (`psp_without_seller`).
    if (saleable && psp.choice === '') return t('admin.countries.catalog.errors.pspRequired');
    if (reason.trim().length === 0) return t('admin.countries.catalog.errors.reasonRequired');
    return null;
  }

  /** Valida e abre a confirmação. */
  function requestConfirm(): void {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setConfirmOpen(true);
  }

  /** Envia o catálogo ao `setMarketConfig` e fecha em sucesso. */
  async function submit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    // Um fuso por linha; trim + descarta vazias, preservando a ordem. Lista
    // vazia (país conhecido cai no compilado) → não envia o campo.
    const parsedTimezones = timezones
      .split('\n')
      .map((z) => z.trim())
      .filter((z) => z.length > 0);
    // #1078: a região só vai quando mudou — o servidor cobra ROOT de quem a troca, e FINANCE
    // salvando o resto do país não pode cair nessa recusa. '' = apagar o override (`null`).
    const regionChanged = region !== (current?.region ?? '');
    try {
      const saved = await setMarketConfigCallable({
        country: countryCode,
        saleable,
        ...(saleable ? marketPspPayload(psp) : { payment_provider: null }),
        name: name.trim(),
        ...(phone.trim().length > 0 ? { phone_country_code: phone.trim() } : {}),
        ...(currency !== '' ? { currency } : {}),
        ...(language !== '' ? { language } : {}),
        ...(parsedTimezones.length > 0 ? { timezones: parsedTimezones } : {}),
        ...(taxName.trim().length > 0 ? { tax_id_name: taxName.trim() } : {}),
        ...(taxRegex.trim().length > 0 ? { tax_id_regex: taxRegex.trim() } : {}),
        ...(taxPlaceholder.trim().length > 0 ? { tax_id_placeholder: taxPlaceholder.trim() } : {}),
        tax_id_required: taxRequired,
        otp_enabled: otpEnabled,
        active,
        ...(regionChanged ? { region: region === '' ? null : region } : {}),
        reason: reason.trim(),
      });
      toast.success(t('admin.countries.catalog.saved'));
      // #756: servidor sem `payment_mode` na resposta é o antigo (janela do
      // deploy) — o modo pode ter sido gravado como live.
      if (saleable && saved.payment_mode === undefined) toast.error(t('billing.markets.staleServer'));
      setConfirmOpen(false);
      onClose();
    } catch (e) {
      setConfirmOpen(false);
      setError(e instanceof Error ? e.message : t('admin.countries.catalog.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        visible={!confirmOpen}
        onDismiss={onClose}
        header={`${flagEmoji(country)} ${t('admin.countries.catalog.editTitle', {
          name: name || country,
        })}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={onClose} disabled={submitting}>
                {t('admin.countries.catalog.cancel')}
              </Button>
              <Button variant="primary" onClick={requestConfirm} disabled={submitting}>
                {t('admin.countries.catalog.save')}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {error && (
            <Box color="text-status-error" variant="p">
              {error}
            </Box>
          )}

          <Container>
            <SpaceBetween size="xs">
              <FormField
                label={<Box variant="strong">{t('admin.countries.catalog.activeLabel')}</Box>}
                description={t('admin.countries.catalog.activeDescription')}
              >
                <Toggle
                  checked={active}
                  disabled={active && hasActiveTenants}
                  onChange={({ detail }): void => { setActive(detail.checked); }}
                >
                  {active ? t('admin.countries.catalog.on') : t('admin.countries.catalog.off')}
                </Toggle>
              </FormField>
              {active && hasActiveTenants && (
                <Alert type="info">
                  {t('admin.countries.catalog.deactivateBlocked', { count: activeTenantCount })}
                </Alert>
              )}
            </SpaceBetween>
          </Container>

          <ColumnLayout columns={2}>
            <FormField label={t('admin.countries.catalog.nameLabel')}>
              <Input value={name} onChange={({ detail }): void => { setName(detail.value); }} />
            </FormField>
            <FormField label={t('admin.countries.catalog.phoneLabel')}>
              <Input
                value={phone}
                onChange={({ detail }): void => { setPhone(detail.value); }}
                placeholder="+55"
              />
            </FormField>
            <FormField label={t('admin.countries.catalog.currencyLabel')}>
              <Select
                selectedOption={CURRENCY_OPTIONS.find((o) => o.value === currency) ?? null}
                options={CURRENCY_OPTIONS}
                placeholder={t('admin.countries.catalog.select')}
                onChange={({ detail }): void =>
                  { setCurrency(detail.selectedOption.value as Currency); }
                }
              />
            </FormField>
            <FormField label={t('admin.countries.catalog.languageLabel')}>
              <Select
                selectedOption={LANGUAGE_OPTIONS.find((o) => o.value === language) ?? null}
                options={LANGUAGE_OPTIONS}
                placeholder={t('admin.countries.catalog.select')}
                onChange={({ detail }): void =>
                  { setLanguage(detail.selectedOption.value as TenantLocale); }
                }
              />
            </FormField>
          </ColumnLayout>

          <FormField
            label={t('admin.countries.catalog.timezonesLabel')}
            description={t('admin.countries.catalog.timezonesHint')}
          >
            <Textarea
              value={timezones}
              onChange={({ detail }): void => { setTimezones(detail.value); }}
              placeholder={'America/Sao_Paulo\nAmerica/Manaus'}
              rows={3}
            />
          </FormField>

          <ColumnLayout columns={2}>
            <FormField label={t('admin.countries.catalog.taxNameLabel')}>
              <Input
                value={taxName}
                onChange={({ detail }): void => { setTaxName(detail.value); }}
                placeholder="CNPJ"
              />
            </FormField>
            <FormField label={t('admin.countries.catalog.taxPlaceholderLabel')}>
              <Input
                value={taxPlaceholder}
                onChange={({ detail }): void => { setTaxPlaceholder(detail.value); }}
                placeholder="00.000.000/0000-00"
              />
            </FormField>
          </ColumnLayout>
          <FormField
            label={t('admin.countries.catalog.taxRegexLabel')}
            description={t('admin.countries.catalog.taxRegexHint')}
          >
            <Input
              value={taxRegex}
              onChange={({ detail }): void => { setTaxRegex(detail.value); }}
              placeholder="^\\d{14}$"
            />
          </FormField>

          <ColumnLayout columns={2}>
            <FormField label={t('admin.countries.catalog.taxRequiredLabel')}>
              <Toggle
                checked={taxRequired}
                onChange={({ detail }): void => { setTaxRequired(detail.checked); }}
              >
                {taxRequired ? t('admin.countries.catalog.yes') : t('admin.countries.catalog.no')}
              </Toggle>
            </FormField>
            <FormField label={t('admin.countries.catalog.otpLabel')}>
              <Toggle
                checked={otpEnabled}
                onChange={({ detail }): void => { setOtpEnabled(detail.checked); }}
              >
                {otpEnabled ? t('admin.countries.catalog.on') : t('admin.countries.catalog.off')}
              </Toggle>
            </FormField>
          </ColumnLayout>

          <FormField label={t('admin.countries.catalog.paymentsLabel')}>
            <Toggle checked={saleable} onChange={({ detail }): void => { setSaleable(detail.checked); }}>
              {saleable
                ? t('admin.countries.catalog.paymentsOn')
                : t('admin.countries.catalog.paymentsOff')}
            </Toggle>
          </FormField>

          {saleable && (
            <MarketPspFields
              country={countryCode}
              value={psp}
              onChange={setPsp}
            />
          )}

          <FormField
            label={t('admin.countries.catalog.region.label')}
            description={t('admin.countries.catalog.region.description')}
            constraintText={isRoot || role === undefined ? undefined : t('admin.countries.catalog.region.rootOnly')}
          >
            <Select
              selectedOption={regionOptions.find((o) => o.value === region) ?? regionOptions[0] ?? null}
              options={regionOptions}
              disabled={!isRoot}
              onChange={({ detail }): void => {
                setRegion((detail.selectedOption.value ?? '') as TenantRegion | '');
              }}
            />
          </FormField>

          <FormField label={t('admin.countries.catalog.reasonLabel')}>
            <Input
              value={reason}
              // Auditoria #1078 (2026-10-29): mesmo teto do `SetMarketConfigInput.reason` (500) — acima
              // dele o `callable()` lançava `ZodError` e o diálogo mostrava o JSON cru.
              onChange={({ detail }): void => { setReason(detail.value.slice(0, 500)); }}
              placeholder={t('admin.countries.catalog.reasonPlaceholder')}
            />
          </FormField>
        </SpaceBetween>
      </Modal>

      <AdminConfirm
        visible={confirmOpen}
        title={t('admin.countries.catalog.confirmTitle')}
        confirmLabel={t('admin.countries.catalog.confirmApply')}
        cancelLabel={t('admin.countries.catalog.cancel')}
        loading={submitting}
        onConfirm={(): void => {
          void submit();
        }}
        onDismiss={(): void => { setConfirmOpen(false); }}
      >
        <Box variant="p">{t('admin.countries.catalog.confirmDescription', { country })}</Box>
      </AdminConfirm>
    </>
  );
}
